// Phase JB.3b — useCommunityLedger hook.
//
// Anon-readable. Returns last 30 days of community_ledger entries
// ordered by at_date DESC. RLS already restricts to ≤30d.
//
// N04/N06: NO filter excludes losses. Wins, losses, and voids all
// surface — that's the doctrine of loss visibility.

import { useEffect, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";

export type LedgerKind =
  | "win"
  | "loss"
  | "milestone"
  | "no_bet"
  | "founder_note"
  | "wc_event"
  | "signal_won"
  | "signal_lost"
  | "signal_void"
  | "slip_won";

export interface LedgerEntry {
  entry_id: string;
  kind: LedgerKind;
  at_date: string;
  headline: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface UseCommunityLedgerResult {
  entries: LedgerEntry[];
  loading: boolean;
  error: string | null;
}

const PAGE_SIZE = 100;

export function useCommunityLedger(): UseCommunityLedgerResult {
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const sb = getBrowserClient();
    setLoading(true);
    setError(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    void (sb.from("community_ledger" as never) as any)
      .select("entry_id, kind, at_date, headline, payload, created_at")
      .order("at_date", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(PAGE_SIZE)
      .then((res: { data: LedgerEntry[] | null; error: { message: string } | null }) => {
        if (cancelled) return;
        if (res.error) setError(res.error.message);
        else setEntries(res.data ?? []);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { entries, loading, error };
}
